import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulCustomIcon,
    RowHeader,
    XStack,
    YStack,
} from 'smoothie';

import {
    formatWithMask,
    PHONE_NUMBER_MASK,
    SSN_MASK,
} from '../../../common/utils/maskUtils';

const formatPhoneNumber = (raw: string): string =>
    formatWithMask(PHONE_NUMBER_MASK, raw).masked;

const formatMaskedTaxId = (raw: string): string => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 0) return raw;
    const { masked } = formatWithMask(SSN_MASK, digits);
    // Mask everything except the last 4 digits: XXX-XX-1234 → ***-**-1234
    return masked.replace(/[\d]/g, (char, index) =>
        index < masked.length - 4 ? '*' : char,
    );
};

export interface ReviewBeneficiaryAccount {
    id: string;
    nickname: string;
    isJointAccount: boolean;
    icon: string;
    color: string;
}

export interface ReviewBeneficiaryData {
    legalFirstName: string;
    legalLastName: string;
    dateOfBirth: string;
    taxId: string;
    phoneNumber: string;
    email: string;
}

interface ReviewBeneficiaryProps {
    data: Partial<ReviewBeneficiaryData> | null;
    accounts: ReviewBeneficiaryAccount[];
    onConfirm: () => void;
    isConfirming?: boolean;
}

const DETAIL_FIELDS: Array<{
    label: string;
    key: keyof ReviewBeneficiaryData;
    format?: (value: string) => string;
    defaultValue?: string;
}> = [
    { label: 'Legal first name', key: 'legalFirstName' },
    { label: 'Legal last name', key: 'legalLastName' },
    { label: 'Date of birth', key: 'dateOfBirth' },
    {
        label: 'Tax Identification Number',
        key: 'taxId',
        format: formatMaskedTaxId,
        defaultValue: '***-**-****',
    },
    { label: 'Phone number', key: 'phoneNumber', format: formatPhoneNumber },
    { label: 'Email address', key: 'email' },
];

const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <XStack gap={8} alignItems="center">
        <YStack flex={1}>
            <BodySmallRegular color="$gray1000">{label}</BodySmallRegular>
        </YStack>
        <BodySmallRegular color="$black" textAlign="right">
            {value}
        </BodySmallRegular>
    </XStack>
);

export const ReviewBeneficiary = ({
    data,
    accounts,
    onConfirm,
    isConfirming = false,
}: ReviewBeneficiaryProps) => {
    if (data == null) {
        return;
    }

    return (
        <YStack gap={24} paddingVertical={24}>
            <BodyMediumRegular color="$gray1000">
                Please review and confirm the details below are accurate. In the
                event of your death, these details will be used to positively
                identify your beneficiary before any funds are paid out.
            </BodyMediumRegular>

            <YStack borderRadius={24} overflow="hidden">
                <RowHeader
                    title="Beneficiary details"
                    marginBottom={0}
                    borderTopLeftRadius={24}
                    borderTopRightRadius={24}
                />
                <YStack gap={16} padding={16} backgroundColor="$gray100">
                    {DETAIL_FIELDS.map(
                        ({ label, key, format, defaultValue }) => {
                            const raw = data[key] ?? defaultValue ?? '';
                            return (
                                <InfoRow
                                    key={key}
                                    label={label}
                                    value={format != null ? format(raw) : raw}
                                />
                            );
                        },
                    )}
                </YStack>
            </YStack>

            <YStack borderRadius={24} overflow="hidden">
                <RowHeader
                    title="Fruitful Cash Accounts"
                    marginBottom={0}
                    borderTopLeftRadius={24}
                    borderTopRightRadius={24}
                />
                {accounts.map((account) => (
                    <RowHeader
                        key={account.id}
                        leftContentOverride={
                            <FruitfulCustomIcon
                                icon={account.icon}
                                color={account.color}
                                size="xs"
                            />
                        }
                        title={account.nickname}
                        label={
                            account.isJointAccount
                                ? 'Joint account'
                                : 'Individual account'
                        }
                        backgroundColor="$gray100"
                        marginBottom={0}
                    />
                ))}
            </YStack>

            <Button
                text="Confirm beneficiary"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                disabled={isConfirming}
                onPress={onConfirm}
            />
        </YStack>
    );
};
